import {connectToDb} from "../connectToDb/connectToDb";
import { Event } from "./Event";
import { EventsManager } from "./eventsManager";

/**
 * Firebase currently allows only one firebase connection. We wrap it in a singleton event connection class
 *
 * @constructor
 */

export class EventFirebaseConnection {

    private eventsManager: EventsManager;
    private telemetry;
    private currentEvent: Event = null;
    private currentUserKey = null;
    private checkConnectionPromise = null;

    constructor(eventsManager: EventsManager) {
        this.eventsManager = eventsManager;
        this.telemetry = this.eventsManager.getTelemetry().clone();
        this.telemetry.context().class = 'EventFirebaseConnection';
    }

    public connect(event: Event) {
        this.telemetry.debug("Connection check pending for event: " + event.getEventId());
        return this.scheduleCheckConnectionCall(() => {
            this.telemetry.debug("Connection check executing for event: " + event.getEventId());
    
            /**
             * When connecting to a event, lets start off with the best active auth the events manager has to offer
             * The EMgr will call _onActiveAuthChanged when it has a new auth for any event,
             * but we have no option but to ignore it at that time because we have no _currentEvent!
             *
             * So we start off the connection with the best active auth the manager has to offer
             */
            const activeAuth = this.eventsManager.getBestActiveAuthForEventId(event.getEventId());
            const activeAuthKey = this.eventsManager.getActiveAuthKey(activeAuth);
            return this.checkConnection(activeAuthKey, event);
        });
    };

    public disconnect() {
        this.telemetry.debug("Connection check pending for event disconnection.");
        return this.scheduleCheckConnectionCall(() => {
            this.telemetry.debug("Connection check executing for event disconnection.");
            return this.checkConnection(this.currentUserKey, null);
        });
    };

    public onActiveAuthChanged(probableActiveAuth?) {
        this.telemetry.debug("Connection check pending for user: " +
            (this.eventsManager.getActiveAuthKey(probableActiveAuth) || "") .substring(0, 4)
        );
    
        return this.scheduleCheckConnectionCall(() => {
    
            /**
             * We make sure that we check for currentEvent inside the _schedule call
             * This ensure that no other _checkConnection calls are running and hence
             * we will always get the right currentEvent
             *
             * The edge case here is that the onActiveAuthChange for the right user comes in
             * after the _checkConnection for the event has started, but before it has completed
             *
             * hence if this is outside the _schedule call, the currentEvent will be null
             *
             */
    
            const eventId = this.currentEvent && this.currentEvent.getEventId();
            if (!eventId) {
                this.telemetry.info({
                    msg: "Ignoring ActiveAuthChange",
                    newUserKey: (this.eventsManager.getActiveAuthKey(this.eventsManager.getBestActiveAuthForEventId(eventId)) || "").substring(0, 4)
                });
                return;
            }
    
            const activeAuth = this.eventsManager.getBestActiveAuthForEventId(eventId);
            const newUserKey = this.eventsManager.getActiveAuthKey(activeAuth);
            const newUserKeyShort = (newUserKey || "").substring(0, 4) + "...";
    
            this.telemetry.debug("Connection check executing for user: " + newUserKeyShort);
    
            return this.checkConnection(newUserKey, this.currentEvent);
        });
    };

    private connect_info(newUser, newEvent: Event, info) {
        return connectToDb(info.connection)
            .then(firebase => {
                this.telemetry.debug("Connected using full event");
                this.currentEvent = newEvent;
                this.currentUserKey = newUser;
                this.currentEvent.onEventConnectionConnect(firebase);
                return null;
            })
            .catch(err => {
                this.telemetry.error("Connect using full info failed!");
                this.telemetry.error(err);
                throw err;
            });
    };
    
    private connect_publicInfo(newUser, newEvent: Event, publicInfo) {
        const deferrable = this.eventsManager.getPromiseLib().defer();
        return connectToDb(publicInfo.connection)
            .then(firebase => {
                this.telemetry.debug("Connected using publicInfo");
                this.currentEvent = newEvent;
                this.currentUserKey = newUser;
                this.currentEvent.onEventConnectionConnect(firebase);
                return null;
            })
            .catch(err => {
                this.telemetry.error("Connect using public info failed!");
                this.telemetry.error(err);
                throw err;
            });
    };

    private checkConnection(newUser, newEvent: Event) {
        const userChanged = (this.currentUserKey !== newUser);
        const eventChanged = (this.currentEvent !== newEvent);
    
        if (userChanged && (newEvent === null)){
            this.telemetry.debug("Connection check ignored, but user accepted");
            this.currentUserKey = newUser;
            return;
        }
    
        this.telemetry.debug({
            userChanged: userChanged,
            eventChanged: eventChanged,
            msg: "Connecting"
        });
    
        let eventInfo = null;
        let eventPublicInfo = null;
    
    
        var promisesToTry = [];
    
        //EventPublicInfo promise
        var eventPublicInfoPromise = this.eventsManager.getEventPublicInfoCache().find(newEvent.getEventId()).then((_eventPublicInfo) => {
            eventPublicInfo = _eventPublicInfo;
        });
    
    
        //EventInfo promise
        //Dont try the event info workflow in case there is no one logged in
        let eventInfoPromise = null;
        if (!!newUser) {
            eventInfoPromise = this.eventsManager.getEventInfoCache().find(newEvent.getEventId()).then((_eventInfo) => {
                eventInfo = _eventInfo;
            });
    
        }
    
        if (eventPublicInfoPromise) {
            promisesToTry.push(eventPublicInfoPromise);
        }
    
        if (eventInfoPromise) {
            promisesToTry.push(eventInfoPromise);
        }
    
    
        const anyInfo = this.eventsManager.getPromiseLib().any(promisesToTry);
        return anyInfo.then(() => {
    
            //If we have the full event info, that's the best case
            //connect and return;
            if (eventInfoPromise && eventInfo) {
                //This._telemetry.debug("Connecting with full event");
                return this.connect_info(newUser, newEvent, eventInfo);
            }
            //This._telemetry.debug("Connecting with publicInfo");
            let ret = this.connect_publicInfo(newUser, newEvent, eventPublicInfo);
    
            //else queue up work to connect using this information and connect with publicInfo
            if (eventInfoPromise) {
                ret = this.eventsManager.getPromiseLib().all([ret, eventInfoPromise]).then(() => {
                    //This._telemetry.debug("Connecting (deferred) with full event");
                    return this.connect_info(newUser, newEvent, eventInfo);
                });
            }
    
            return ret;
        });
    };

    private scheduleCheckConnectionCall(f) {
        var previousCalls = (() => {
    
            //If a previous call is happening, we just use its promise.
            if (this.checkConnectionPromise) {
                return this.checkConnectionPromise;
            }
    
            //If its not, we create a promise which is already resolved and have the rest of the function use this
            //This makes sure that the rest of the function always has a promise to work with.
            var d = this.eventsManager.getPromiseLib().defer();
            d.resolve();
            return d.promise;
        })();
    
    
        this.checkConnectionPromise = previousCalls
            .then(f)
            .then(() => {
                this.telemetry.debug("Connection check completed successfully");
            })
            .catch((err) => {
                this.telemetry.error("Connection check completed with error");
                this.telemetry.error(err);
            });
    
        return this.checkConnectionPromise;
    };
    
    /**
     * Disconnect the events connection and return a promise representing this disconnection.
     * @param event
     */
    // THIS APPEARS TO BE A DUPE FUNCTION 
    // public disconnect(event: Event) {
    //     return this.eventsManager.getPromiseLib()((resolve, reject) => {
    //         return reject(new Error("Disconnect not implemented yet"));
    //     });
    // };
}
